import { useMutation, useQuery, useQueryClient } from "react-query";
import { axiosInstance } from "@src/api/axiosConfig";
import { axiosPath } from "@src/api/axiosPath";
import {
    InsertLectureRequestDto,
    LectureListFilterType,
    LectureListResponse,
    LectureRegisterFilterType,
    LectureRegisterListResponse,
    UpdateLectureRequestDto,
} from "./types";

export const initLectureListFilters: LectureListFilterType = {
    page: 1,
    limit: 10,
};

export const initLectureRegisterFilters: LectureRegisterFilterType = {
    page: 1,
    limit: 10,
};

// 강의 목록 조회
const fetchLectureList = async (
    params: LectureListFilterType,
): Promise<LectureListResponse> => {
    const response = await axiosInstance.get(axiosPath.lecture, { params });

    return response.data;
};

export const useLectureList = (
    params: LectureListFilterType,
    enabled: boolean = true,
) => {
    return useQuery<LectureListResponse, Error>(
        ["lectureList", params],
        () => fetchLectureList(params),
        { enabled, keepPreviousData: true },
    );
};

// 상품 코드 중복 확인 (CommodityCreateContents의 useGetCheckCommodityCode와 동일한 API)
export const checkProductCodeDuplicated = async (
    code: string,
): Promise<boolean> => {
    const response = await axiosInstance.get(
        `${axiosPath.nestProducts}/${code}/is-duplicated`,
    );

    return response.data;
};

// 강의 등록
const createLecture = async (
    data: InsertLectureRequestDto,
): Promise<number> => {
    const response = await axiosInstance.post(axiosPath.lecture, data);

    return response.data;
};

export const useCreateLecture = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: InsertLectureRequestDto) => createLecture(data),
        onSuccess: () => {
            // refetchInactive: 목록 페이지가 unmount된 상태에서도 재요청되도록 (전역 refetchOnMount: false 대응)
            queryClient.invalidateQueries("lectureList", { refetchInactive: true });
            queryClient.invalidateQueries("lectureRegisters", { refetchInactive: true });
        },
    });
};

// 강의 수정
const updateLecture = async (
    lectureNo: number,
    data: UpdateLectureRequestDto,
): Promise<number> => {
    const response = await axiosInstance.put(
        `${axiosPath.lecture}/${lectureNo}`,
        data,
    );

    return response.data;
};

export const useUpdateLecture = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            lectureNo,
            data,
        }: {
            lectureNo: number;
            data: UpdateLectureRequestDto;
        }) => updateLecture(lectureNo, data),
        onSuccess: () => {
            // refetchInactive: 목록 페이지가 unmount된 상태에서도 재요청되도록 (전역 refetchOnMount: false 대응)
            queryClient.invalidateQueries("lectureList", { refetchInactive: true });
            queryClient.invalidateQueries("lectureRegisters", { refetchInactive: true });
        },
    });
};

// 강의 등록자 목록 조회
const fetchLectureRegisters = async (
    lectureNo: number,
    params: LectureRegisterFilterType,
): Promise<LectureRegisterListResponse> => {
    const response = await axiosInstance.get(
        `${axiosPath.lecture}/${lectureNo}/registers`,
        { params },
    );

    return response.data;
};

export const useLectureRegisters = (
    lectureNo: number | undefined,
    params: LectureRegisterFilterType,
) => {
    return useQuery<LectureRegisterListResponse, Error>(
        ["lectureRegisters", lectureNo, params],
        () => fetchLectureRegisters(Number(lectureNo), params),
        { enabled: Boolean(lectureNo), keepPreviousData: true },
    );
};
